import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Church, Calendar, MapPin, Clock, Trash2, HandHeart } from 'lucide-react';
import useDemoDates from '@/hooks/useDemoDates';
import AddDateDialog from './AddDateDialog';
import { AddToCalendarButton } from '@/components/native/ContactActionButtons';

const CommunityTab = () => {
  const { dates, loading, addDate, deleteDate } = useDemoDates('community');
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this event? You can always add it again later.')) return;
    await deleteDate(id);
  };

  const handleDateAdded = async (dateData) => {
    await addDate(dateData);
    setDialogOpen(false);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Community & Church Events</h3>
          <p className="text-sm text-gray-500">Group responsibilities and recurring commitments</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900 whitespace-nowrap flex-shrink-0"
          data-testid="add-community-event-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Event
        </Button>
      </div>

      {dates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <Church className="h-12 w-12 mx-auto text-[#C5D9ED] mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Track Community Commitments</h4>
          <p className="text-gray-600 mb-4">
            Keep track of church, volunteering, and community responsibilities
          </p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-[#C5D9ED] hover:bg-[#B0CCE3] text-gray-900"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Event
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {dates.map((item) => (
            <Card key={item.id} className="border-l-4 border-l-[#C5D9ED]">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <h4 className="font-medium text-gray-900">{item.title}</h4>
                      {item.organization && (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700">
                          {item.organization}
                        </Badge>
                      )}
                      {item.repeat_frequency && item.repeat_frequency !== 'none' && (
                        <Badge variant="outline">
                          {item.repeat_frequency}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex items-center gap-4 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          {new Date(item.date + 'T00:00:00').toLocaleDateString('en-US', { 
                            weekday: 'short', month: 'short', day: 'numeric' 
                          })}
                        </span>
                        {item.time && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3.5 w-3.5" />
                            {item.time}
                          </span>
                        )}
                        {item.location && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {item.location}
                          </span>
                        )}
                      </div>
                      
                      {/* Offered to provide - prominent display */}
                      {item.offered_to_provide && (
                        <div className="mt-2 p-2 bg-green-50 rounded border border-green-200">
                          <div className="flex items-center gap-1 text-green-700 font-medium">
                            <HandHeart className="h-4 w-4" />
                            You offered to provide:
                          </div>
                          <p className="text-green-800 ml-5">{item.offered_to_provide}</p>
                        </div>
                      )}
                      
                      {item.notes && (
                        <p className="text-gray-500 italic mt-1">{item.notes}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <AddToCalendarButton
                      dateRecord={{
                        title: item.title,
                        date: item.date,
                        time: item.time,
                        location: item.location || '',
                        type: 'community',
                        recurrence: item.repeat_frequency !== 'none' ? item.repeat_frequency : null,
                        all_day: !item.time,
                        reminder_minutes: 60,
                        notes: item.offered_to_provide || item.notes || ''
                      }}
                      size="sm"
                      variant="ghost"
                      className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                      data-testid={`add-to-calendar-${item.id}`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(item.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AddDateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        category="community"
        onSuccess={handleDateAdded}
      />
    </>
  );
};

export default CommunityTab;
